import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import VideoPlayer from "@/components/VideoPlayer";

function BonusResources({ user, onLogout }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [watchedVideos, setWatchedVideos] = useState({});

  const bonusVideos = [
    {
      id: "customer-service",
      title: "Customer Service Essentials",
      description: "Learn the fundamentals of providing excellent customer service for your business. Discover how to build lasting relationships with your customers.",
      videoUrl: "https://customer-assets.emergentagent.com/job_learn-lemons/artifacts/qaee8kdu_Customer%20Service%20Essentials.mp4",
      icon: "🤝",
      duration: "Educational Video"
    },
    {
      id: "sell-with-heart",
      title: "Sell With Heart",
      description: "Master the art of authentic selling. Learn how to connect with customers genuinely and create meaningful business relationships.",
      videoUrl: "https://customer-assets.emergentagent.com/job_learn-lemons/artifacts/zba5ddv1_Sell%20With%20Heart.mp4",
      icon: "❤️",
      duration: "Educational Video"
    },
    {
      id: "confidence-pitch",
      title: "Confidence Before You Pitch",
      description: "Build the confidence you need to present your business ideas effectively. Learn techniques to overcome nervousness and deliver powerful pitches.",
      videoUrl: "https://customer-assets.emergentagent.com/job_learn-lemons/artifacts/gkbsmf59_Confidence%20Before%20You%20Pitch.mp4",
      icon: "💪",
      duration: "Educational Video"
    },
    {
      id: "handling-rejection",
      title: "What to Do If Someone Says No",
      description: "Learn how to handle rejection gracefully and turn 'no' into an opportunity for growth. Develop resilience and persistence in your entrepreneurial journey.",
      videoUrl: "https://customer-assets.emergentagent.com/job_learn-lemons/artifacts/3tfk793a_What%20to%20Do%20If%20Someone%20Says%20No.mp4",
      icon: "🚫➡️✅",
      duration: "Educational Video"
    },
    {
      id: "what-is-entrepreneurship",
      title: "What Is Entrepreneurship?",
      description: "Explore the fundamentals of entrepreneurship and what it means to be an entrepreneur. Understand the mindset and skills needed for business success.",
      videoUrl: "https://customer-assets.emergentagent.com/job_learn-lemons/artifacts/40txb35v_What%20Is%20Entrepreneurship_.mp4",
      icon: "🚀",
      duration: "Educational Video"
    },
    {
      id: "counting-money",
      title: "Counting Money the Right Way",
      description: "Master essential money counting and cash handling skills for your business. Learn accurate counting techniques and best practices for managing cash transactions.",
      videoUrl: "https://customer-assets.emergentagent.com/job_learn-lemons/artifacts/zi9d73j0_Counting%20Money%20the%20Right%20Way.mp4",
      icon: "💰",
      duration: "Educational Video"
    },
    {
      id: "teamwork-action",
      title: "Teamwork in Action",
      description: "See real examples of effective teamwork in business. Learn how collaboration and cooperation lead to better outcomes and stronger businesses.",
      videoUrl: "https://customer-assets.emergentagent.com/job_learn-lemons/artifacts/qp6kmt5e_Teamwork%20in%20Action%20%281%29.mp4",
      icon: "🤝👥",
      duration: "Educational Video"
    }
  ];

  const handleVideoEnd = (videoId) => {
    setWatchedVideos(prev => ({ ...prev, [videoId]: true }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      <Navbar user={user} onLogout={onLogout} />

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="text-6xl mb-4">🎁</div>
          <h1 className="text-5xl font-bold text-gray-800 mb-4">
            Bonus Resources
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Extra learning materials to enhance your entrepreneurial skills!
            These videos are optional but highly recommended.
          </p>
        </div>

        {/* Info Banner */}
        <div className="bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-300 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="text-4xl">💡</div>
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Additional Learning</h3>
              <p className="text-gray-700">
                These bonus videos are not required to complete the course, but they provide 
                valuable insights to help you succeed in your business journey. Watch them at your own pace!
              </p>
            </div>
          </div>
        </div>

        {/* Video Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {bonusVideos.map((video) => (
            <div 
              key={video.id}
              className="bg-white rounded-2xl shadow-xl overflow-hidden transform hover:scale-105 transition-transform"
            >
              {/* Card Header */}
              <div className="bg-gradient-to-r from-purple-400 to-pink-400 p-6 text-white">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-4xl">{video.icon}</span>
                  <div>
                    <h2 className="text-2xl font-bold">{video.title}</h2>
                    <p className="text-sm opacity-90">{video.duration}</p>
                  </div>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-6">
                <p className="text-gray-700 mb-4">
                  {video.description}
                </p>

                {/* Video Player */}
                <div className="relative">
                  <VideoPlayer
                    src={video.videoUrl}
                    onEnded={() => handleVideoEnd(video.id)}
                    poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='450'%3E%3Crect width='800' height='450' fill='%23f3e8ff'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%23a855f7' font-size='48' font-family='Arial'%3E▶ Play Video%3C/text%3E%3C/svg%3E"
                  />

                  {/* Watched Badge */}
                  {watchedVideos[video.id] && (
                    <div className="absolute top-2 right-2 bg-green-500 text-white px-4 py-2 rounded-full font-semibold shadow-lg flex items-center gap-2">
                      <span>✓</span> {t('video.watched', 'Watched')}
                    </div>
                  )}
                </div>

                {/* Watch Status */}
                <div className="mt-4 text-center">
                  {watchedVideos[video.id] ? (
                    <div className="text-green-600 font-semibold">
                      ✅ {t('video.youveWatched', "You've watched this video!")}
                    </div>
                  ) : (
                    <div className="text-gray-500 text-sm">
                      {t('video.clickToStart', 'Click play to start watching')}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Progress Indicator */}
        <div className="bg-white rounded-xl shadow-lg p-6 text-center">
          <h3 className="text-xl font-bold text-gray-800 mb-3">Your Progress</h3>
          <div className="text-4xl font-bold text-purple-600 mb-2">
            {Object.keys(watchedVideos).length} / {bonusVideos.length}
          </div>
          <p className="text-gray-600">Bonus videos watched</p>
          
          {Object.keys(watchedVideos).length === bonusVideos.length && (
            <div className="mt-4 bg-gradient-to-r from-purple-100 to-pink-100 border-2 border-purple-300 rounded-lg p-4">
              <div className="text-3xl mb-2">🌟</div>
              <p className="font-bold text-purple-700">
                Awesome! You&apos;ve watched all bonus content!
              </p>
            </div>
          )}
        </div>

        {/* Back Button */}
        <div className="text-center mt-8">
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

export default BonusResources;
